import { FC, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { format } from 'date-fns';
import atLocale from 'date-fns/locale/de-AT';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  TextField,
  Typography
} from '@mui/material';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import axios from 'src/lib/axios';
import { CalendarCancelInfoDTO } from 'src/api/dto/booking/calendarCancel';

const BookingCancel: FC = () => {
  const isMountedRef = useIsMountedRef();
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const guid = query.get('guid');

  const [loading, setLoading] = useState(true);
  const [loadErrorText, setLoadErrorText] = useState('');
  const [info, setInfo] = useState<CalendarCancelInfoDTO | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [cancelErrorText, setCancelErrorText] = useState('');

  const loadInfo = useCallback(async () => {
    try {
      const response = await axios.get<CalendarCancelInfoDTO>(`/api/calendar/booking/cancel/${guid}`);
      if (isMountedRef.current) {
        setInfo(response.data);
        setLoading(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setLoadErrorText(err.message);
        setLoading(false);
      }
    }
  }, [isMountedRef]);

  useEffect(() => {
    if (guid) {
      loadInfo();
    } else {
      setLoadErrorText('Es wurde kein gültiger Link zur Terminstornierung übergeben.');
      setLoading(false);
    }
  }, [isMountedRef]);

  const formatDateTime = (value: string) => format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: atLocale });

  return (
    <>
      <Helmet>
        <title>Kumao | Termin stornieren</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <Container
          maxWidth="sm"
          sx={{ py: '80px' }}
        >
          <Card>
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 4
              }}
            >
              {loading && (
                <Typography
                  color="textPrimary"
                  gutterBottom
                  variant="h4"
                >
                  Bitte Warten...
                </Typography>
              )}

              {!loading && loadErrorText && (
                <div>
                  <Typography
                    color="textPrimary"
                    gutterBottom
                    variant="h5"
                  >
                    Der Termin konnte nicht gefunden werden
                  </Typography>
                  <Alert severity="error">{loadErrorText}</Alert>
                </div>
              )}

              {!loading && !loadErrorText && info && !cancelled && (
                <div>
                  <Typography
                    color="textPrimary"
                    gutterBottom
                    variant="h5"
                  >
                    Termin stornieren
                  </Typography>
                  <Typography
                    color="textSecondary"
                    variant="body1"
                    sx={{ mt: 1 }}
                  >
                    {info.serviceDescription}
                    {' '}
                    bei
                    {' '}
                    {info.organizationName}
                    <br />
                    {formatDateTime(info.start)}
                    {' '}
                    -
                    {' '}
                    {formatDateTime(info.end)}
                  </Typography>

                  {!info.cancellable && (
                    <Alert
                      severity="warning"
                      sx={{ mt: 3 }}
                    >
                      Dieser Termin liegt in der Vergangenheit und kann nicht mehr storniert werden.
                    </Alert>
                  )}

                  {info.cancellable && (
                    <Formik
                      initialValues={{
                        voidingReason: '',
                        submit: null
                      }}
                      validationSchema={Yup.object().shape({
                        voidingReason: Yup.string().max(500).required('Bitte gib einen Stornierungsgrund an')
                      })}
                      onSubmit={async (values, { setSubmitting }): Promise<void> => {
                        try {
                          setCancelErrorText('');
                          await axios.post(`/api/calendar/booking/cancel/${guid}`, { voidingReason: values.voidingReason });
                          if (isMountedRef.current) {
                            setCancelled(true);
                            setSubmitting(false);
                          }
                        } catch (err) {
                          if (isMountedRef.current) {
                            setCancelErrorText(err.message);
                            setSubmitting(false);
                          }
                        }
                      }}
                    >
                      {({
                        errors,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                        touched,
                        values
                      }): JSX.Element => (
                        <form
                          noValidate
                          onSubmit={handleSubmit}
                        >
                          <Divider sx={{ my: 3 }} />
                          <TextField
                            error={Boolean(touched.voidingReason && errors.voidingReason)}
                            fullWidth
                            required
                            multiline
                            rows={3}
                            helperText={touched.voidingReason && errors.voidingReason}
                            label="Stornierungsgrund"
                            margin="normal"
                            name="voidingReason"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.voidingReason}
                            variant="outlined"
                          />
                          {cancelErrorText && (
                            <Alert
                              severity="error"
                              sx={{ mt: 2 }}
                            >
                              {cancelErrorText}
                            </Alert>
                          )}
                          <Box mt={2}>
                            <Button
                              color="primary"
                              disabled={isSubmitting || !values.voidingReason}
                              fullWidth
                              size="large"
                              type="submit"
                              variant="contained"
                              name="btnCancelBooking"
                            >
                              Termin stornieren
                            </Button>
                          </Box>
                        </form>
                      )}
                    </Formik>
                  )}
                </div>
              )}

              {cancelled && (
                <div>
                  <Typography
                    color="textPrimary"
                    gutterBottom
                    variant="h4"
                  >
                    Dein Termin wurde storniert.
                  </Typography>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                  >
                    Der Dienstleister wurde über die Stornierung benachrichtigt und du erhältst in Kürze eine Bestätigung per E-Mail.
                  </Typography>
                  <Box mt={3}>
                    <Button
                      color="primary"
                      component={RouterLink}
                      to="/"
                      size="large"
                      variant="contained"
                    >
                      Zur Startseite
                    </Button>
                  </Box>
                </div>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default BookingCancel;
